import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3 } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/DataTable'
import { useArAging } from '@/features/invoicing/queries'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import { getUserLocale } from '@/lib/locale'
import type { ArAgingCustomerRow, ArAgingInvoice } from '@/features/invoicing/types'

/**
 * AR Aging (Phase 2d). Standard bucketing (Current, 1–30, 31–60, 61–90, 91+)
 * computed from open invoices for the tenant. Reuses the existing
 * /api/invoicing/reports/ar-aging endpoint so the numbers reconcile with
 * the dashboard AR widget and the AR control account on the Balance Sheet.
 */
export function ArAgingPage() {
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const [asOf, setAsOf] = useState<string>(todayISO)

  const { data, isLoading } = useArAging(asOf)
  const report = data?.report
  // Currency for the report header. Prefer the first invoice's currency
  // (handles multi-currency tenants), but when there are zero invoices
  // (empty workspace, fresh tenant) fall back to the tenant's regional
  // currency instead of a hardcoded USD - a UK tenant with no invoices
  // yet should still see GBP totals, not "$0.00".
  const currency = useMemo(() => {
    const first = report?.invoices[0]?.currency
    return first || getUserLocale().currency
  }, [report])

  const customerColumns = useMemo(() => [
    { accessorKey: 'customer_display_name', header: 'Customer',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) => row.original.customer_display_name,
    },
    { accessorKey: 'current', header: 'Current',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) =>
        formatInvoiceMoney(row.original.current, currency),
    },
    { accessorKey: 'd1_30', header: '1–30',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) =>
        formatInvoiceMoney(row.original.d1_30, currency),
    },
    { accessorKey: 'd31_60', header: '31–60',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) =>
        formatInvoiceMoney(row.original.d31_60, currency),
    },
    { accessorKey: 'd61_90', header: '61–90',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) =>
        formatInvoiceMoney(row.original.d61_90, currency),
    },
    { accessorKey: 'd91_plus', header: '91+',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) =>
        formatInvoiceMoney(row.original.d91_plus, currency),
    },
    { accessorKey: 'total', header: 'Total',
      cell: ({ row }: { row: { original: ArAgingCustomerRow } }) => (
        <span className="font-semibold">{formatInvoiceMoney(row.original.total, currency)}</span>
      ),
    },
  ], [currency])

  const invoiceColumns = useMemo(() => [
    { accessorKey: 'invoice_number', header: 'Invoice',
      cell: ({ row }: { row: { original: ArAgingInvoice } }) => (
        <Link to={`/invoicing/invoices/${row.original.id}`} className="font-medium text-primary hover:underline">
          {row.original.invoice_number}
        </Link>
      ),
    },
    { accessorKey: 'customer_display_name', header: 'Customer' },
    { accessorKey: 'due_date', header: 'Due' },
    { accessorKey: 'days_past_due', header: 'Days past',
      cell: ({ row }: { row: { original: ArAgingInvoice } }) => (
        row.original.days_past_due > 0
          ? row.original.days_past_due
          : <span className="text-muted-foreground">-</span>
      ),
    },
    { accessorKey: 'balance', header: 'Balance',
      cell: ({ row }: { row: { original: ArAgingInvoice } }) =>
        formatInvoiceMoney(row.original.balance, row.original.currency),
    },
  ], [])

  return (
    <Page
      title="AR Aging"
      description="Outstanding balances bucketed by how far past due they are. Numbers reconcile to the AR control account on the Balance Sheet."
    >
      <Toolbar align="start" className="mb-3">
        <FormField
          label="As of"
          type="date"
          value={asOf}
          onChange={e => setAsOf(e.target.value)}
          className="w-48"
        />
      </Toolbar>
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : !report ? (
        <Card><EmptyState icon={BarChart3} title="No AR aging data" description="Once invoices are outstanding, this report will populate." /></Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-6">
              {report.buckets.map(b => (
                <div key={b.key} className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{b.label}</div>
                  <div className="mt-1 text-lg font-semibold">
                    {formatInvoiceMoney(report.totals[b.key], currency)}
                  </div>
                </div>
              ))}
              <div className="rounded-md border bg-muted/40 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Total</div>
                <div className="mt-1 text-lg font-bold">
                  {formatInvoiceMoney(report.totals.total, currency)}
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">By customer</div>
              <DataTable
                columns={customerColumns as never}
                rows={report.customers}
                empty={
                  <EmptyState icon={BarChart3} title="No outstanding customers" description="Everyone is paid up." />
                }
              />
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">By invoice</div>
              <DataTable
                columns={invoiceColumns as never}
                rows={report.invoices}
                empty={
                  <EmptyState icon={BarChart3} title="No open invoices" description="Nothing outstanding as of this date." />
                }
              />
            </div>
          </Card>
        </div>
      )}
    </Page>
  )
}
